import { Color, Spacing } from '@persona-id/design-tokens';
import { Dispatch, MutableRefObject, RefObject, SetStateAction, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCaretStacked from 'images/icons/caret-stacked.svg';

const Container = styled.div<{ disabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing[1]};
  ${({ disabled }) => {
    if (disabled) {
      return css`
        pointer-events: none;
        transition: opacity ${TransitionDuration.Fast};
        opacity: 0.3;
      `;
    }
  }};
`;

const SliderContainer = styled.div`
  width: calc(100% + 20px);
  height: 58px;
  display: flex;
  flex-direction: row;
  padding-top: ${Spacing[2]};
  user-select: none;
  cursor: default;
`;

const LeftBlock = styled.div<{ percentage: number }>`
  background-color: ${Color.CorePurple600};
  height: 100%;
  justify-content: center;
  color: ${Color.PrimaryWhite};
  border-radius: ${({ percentage }) =>
    percentage === 100 ? `${Spacing[2]}` : `${Spacing[2]} 0px 0px ${Spacing[2]}`};

  ${({ percentage }) => {
    return css`
      width: ${percentage}%;
    `;
  }};
`;

const SliderButton = styled.span`
  height: 100%;
  width: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  position: relative;
  right: 10px;
  z-index: ${Z_INDICES.modal + 1};
`;

const RightBlock = styled.div<{ percentage: number }>`
  background-color: ${Color.CoreSlate100};
  height: 100%;
  justify-content: center;
  color: ${Color.CoreSlate1000};
  position: relative;
  right: 20px;
  border-radius: ${({ percentage }) =>
    percentage === 0 ? `${Spacing[2]}` : `0px ${Spacing[2]} ${Spacing[2]} 0px`};

  ${({ percentage }) => {
    return css`
      width: ${100 - percentage}%;
    `;
  }};
`;

const PercentageText = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: ${FontWeight.Medium};
`;

const StyledIconCaretStacked = styled(IconCaretStacked)`
  background-color: ${Color.PrimaryWhite};
  transform: rotate(90deg);
  color: ${Color.CorePurple800};
`;

const DescriptorsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftDescriptor = styled(Paragraph)`
  display: flex;
  color: ${Color.CorePurple600};
`;

const RightDescriptor = styled(Paragraph)`
  display: flex;
`;

export interface SliderProps {
  isDraggingRef: MutableRefObject<boolean>;
  percentage: number;
  setPercentage: Dispatch<SetStateAction<number>>;
  boundingSectionRef: RefObject<HTMLDivElement>;
  leftDescriptor: string;
  rightDescriptor: string;
  disabled: boolean;
}

export const Slider = ({
  isDraggingRef,
  percentage,
  setPercentage,
  boundingSectionRef,
  leftDescriptor,
  rightDescriptor,
  disabled,
}: SliderProps) => {
  const handleOnMouseDown = () => {
    isDraggingRef.current = true;
  };

  const handleOnMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current || boundingSectionRef.current == null) {
      return;
    }
    const boundingSectionRect = boundingSectionRef.current.getBoundingClientRect();
    const sliderWidth = boundingSectionRect.width;
    const newPercentage = ((e.clientX - boundingSectionRect.left) * 100) / sliderWidth;
    if (newPercentage >= -0.5 && newPercentage <= 100.49) {
      setPercentage(Math.round(newPercentage));
    }
  };

  const handleOnMouseUp = () => {
    isDraggingRef.current = false;
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleOnMouseUp);
    document.addEventListener('mousemove', handleOnMouseMove);

    return () => {
      document.removeEventListener('mouseup', handleOnMouseUp);
      document.removeEventListener('mousemove', handleOnMouseMove);
    };
  });

  return (
    <Container disabled={disabled}>
      <SliderContainer>
        <LeftBlock percentage={percentage}>
          {percentage > 10 && (
            <PercentageText>{Math.round(percentage).toString() + '%'}</PercentageText>
          )}
        </LeftBlock>
        <SliderButton aria-label='rollout' onMouseDown={handleOnMouseDown}>
          <StyledIconCaretStacked />
        </SliderButton>
        <RightBlock percentage={percentage}>
          {percentage < 90 && (
            <PercentageText>{(100 - Math.round(percentage)).toString() + '%'}</PercentageText>
          )}
        </RightBlock>
      </SliderContainer>
      <DescriptorsContainer>
        <LeftDescriptor size='xs'>{leftDescriptor}</LeftDescriptor>
        <RightDescriptor size='xs'>{rightDescriptor}</RightDescriptor>
      </DescriptorsContainer>
    </Container>
  );
};
